<template>
  <div class="st-success-page">
    <div class="st-success-card">
      <img
        src="/assets/register_success.png"
        alt="Transfer Processing"
        class="st-success-icon"
      />
      <h2 class="st-success-title">Transfer Processing</h2>
      <p class="st-success-subtitle">Please wait for the transfer to be processed</p>

      <div class="st-success-details">
        <div class="st-success-row">
          <span class="st-success-label">Transaction ID</span>
          <span class="st-success-value">{{ transactionId }}</span>
        </div>
        <div class="st-success-row">
          <span class="st-success-label">Date &amp; Time</span>
          <span class="st-success-value">{{ formattedDateTime }}</span>
        </div>
        <div class="st-success-row">
          <span class="st-success-label">Amount</span>
          <span class="st-success-value">IDR {{ formatCurrency(amount) }}</span>
        </div>
        <div class="st-success-row">
          <span class="st-success-label">Admin Fee</span>
          <span class="st-success-value">IDR {{ formatCurrency(adminFee) }}</span>
        </div>
        <div class="st-success-divider"></div>
        <div class="st-success-row st-success-row--total">
          <span class="st-success-label">Total</span>
          <span class="st-success-value st-success-value--total">
            IDR {{ formatCurrency(totalAmount) }}
          </span>
        </div>
      </div>

      <button type="button" class="st-success-ok" @click="handleOk">OK</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";

const router = useRouter();
const route = useRoute();
const store = useStore();

store.state.hideConfigButton = false;
store.state.showTransferNavbar = true;
store.state.showNavbar = false;
store.state.showSidenav = false;
store.state.showFooter = false;

const transactionId = ref("-");
const paymentDate = ref("");
const amount = ref(0);
const adminFee = ref(0);
const totalAmount = ref(0);

const formattedDateTime = computed(() => {
  if (!paymentDate.value) return "-";
  const date = new Date(paymentDate.value);
  if (isNaN(date.getTime())) return paymentDate.value;
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
});

function formatCurrency(value) {
  if (value === null || value === undefined) return "0";
  return Number(value).toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

onMounted(() => {
  const state = history.state;
  if (state) {
    transactionId.value = state.transactionId || "-";
    paymentDate.value = state.paymentDate || "";
    amount.value = state.amount ?? 0;
    adminFee.value = state.adminFee ?? 0;
    totalAmount.value = state.totalAmount ?? 0;
  }

  if (transactionId.value === "-" && route.query.transactionId) {
    transactionId.value = route.query.transactionId;
  }
  if (!paymentDate.value && route.query.paymentDate) {
    paymentDate.value = route.query.paymentDate;
  }
  if (!amount.value && route.query.amount) {
    amount.value = parseFloat(route.query.amount);
  }
  if (!adminFee.value && route.query.adminFee) {
    adminFee.value = parseFloat(route.query.adminFee);
  }
  if (!totalAmount.value && route.query.totalAmount) {
    totalAmount.value = parseFloat(route.query.totalAmount);
  }
});

function handleOk() {
  router.push({ name: "SingleTransfer" });
}
</script>

<style scoped>
.st-success-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f2f5;
  padding: 80px 24px 40px;
}

.st-success-card {
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(13, 43, 62, 0.08);
  padding: 40px 32px;
  width: 100%;
  max-width: 520px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.st-success-icon {
  width: 96px;
  height: 96px;
  margin-bottom: 20px;
}

.st-success-title {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #0d2b3e;
  margin: 0 0 8px;
}

.st-success-subtitle {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  color: #7a8b95;
  margin: 0 0 28px;
}

.st-success-details {
  width: 100%;
  border: 1px solid #e2e8ee;
  border-radius: 16px;
  padding: 8px 20px;
  margin-bottom: 28px;
  text-align: left;
}

.st-success-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 0;
}

.st-success-row:not(:last-child):not(.st-success-row--total) {
  border-bottom: 1px solid #f0f2f5;
}

.st-success-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #7a8b95;
  flex-shrink: 0;
}

.st-success-value {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #0d2b3e;
  text-align: right;
  word-break: break-all;
}

.st-success-divider {
  border-top: 1px dashed #d8dde3;
  margin: 4px 0;
}

.st-success-row--total {
  align-items: center;
  padding-top: 16px;
  padding-bottom: 8px;
}

.st-success-value--total {
  font-size: 22px;
  font-weight: 700;
}

.st-success-ok {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 100px;
  background-color: #43bb75;
  color: #ffffff;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(67, 187, 117, 0.3);
  transition: background-color 0.2s ease;
}

.st-success-ok:hover {
  background-color: #37a865;
}
</style>
